import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Save, Code, Info, Eye, Type, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { billingTemplatesApi, agencyConfigApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { CreateBillingTemplateRequest, AgencyConfiguration, TemplateType } from '@/types'

type EditorMode = 'visual' | 'html' | 'preview'

interface BillingTemplateFormPageProps {
  mode?: 'create' | 'edit' | 'view'
}

// Sample data for preview (used when agency config is not available)
const SAMPLE_DATA: Record<string, string> = {
  // Company info - comes from Agency Configuration
  COMPANY_NAME: 'Your Company Name',
  COMPANY_LOGO: '',
  COMPANY_ADDRESS_LINE1: '123 Business Street',
  COMPANY_ADDRESS_LINE2: 'Suite 100',
  COMPANY_CITY: 'San Francisco',
  COMPANY_STATE: 'CA',
  COMPANY_POSTAL_CODE: '94102',
  COMPANY_COUNTRY: 'United States',
  COMPANY_EMAIL: 'billing@yourcompany.com',
  COMPANY_PHONE: '+1 (555) 123-4567',
  COMPANY_WEBSITE: 'www.yourcompany.com',
  COMPANY_TAX_ID: 'US123456789',
  COMPANY_TAX_REG_NUMBER: 'REG123456',
  // Bank details - comes from Agency Configuration Payment Info
  BANK_NAME: 'First National Bank',
  BANK_ACCOUNT_NAME: 'Your Company LLC',
  BANK_ACCOUNT_NUMBER: '****1234',
  BANK_ROUTING_NUMBER: '****5678',
  BANK_SWIFT_CODE: 'FNBKUS33',
  BANK_IBAN: 'US12 3456 7890 1234 5678 90',
  PAYMENT_INSTRUCTIONS: 'Please include invoice number in payment reference.',
  // Client info - comes from invoice client data
  CLIENT_NAME: 'John Smith',
  CLIENT_EMAIL: 'john.smith@example.com',
  CLIENT_PHONE: '+1 (555) 987-6543',
  CLIENT_ADDRESS_LINE1: '456 Client Avenue',
  CLIENT_ADDRESS_LINE2: 'Apt 2B',
  CLIENT_CITY: 'New York',
  CLIENT_STATE: 'NY',
  CLIENT_POSTAL_CODE: '10001',
  CLIENT_COUNTRY: 'United States',
  // Invoice details - comes from invoice data
  INVOICE_NUMBER: 'INV-2025-00001',
  INVOICE_DATE: '10 Dec 2025',
  DUE_DATE: '10 Jan 2026',
  CURRENCY_SYMBOL: '$',
  SUBTOTAL: '1,500.00',
  TAX_RATE: '10',
  TAX_AMOUNT: '150.00',
  DISCOUNT_AMOUNT: '50.00',
  TOTAL_AMOUNT: '1,600.00',
  BALANCE_DUE: '1,600.00',
  AMOUNT_PAID: '0.00',
  NOTES: 'Thank you for your business! Payment is due within 30 days.',
  PAYMENT_TERMS: 'Net 30 - Please pay within 30 days of invoice date.',
  LINE_ITEMS: `<tr>
    <td>Website Design & Development</td>
    <td style="text-align: center;">1</td>
    <td style="text-align: right;">$1,000.00</td>
    <td style="text-align: right;">$1,000.00</td>
  </tr>
  <tr>
    <td>Monthly Hosting (12 months)</td>
    <td style="text-align: center;">12</td>
    <td style="text-align: right;">$25.00</td>
    <td style="text-align: right;">$300.00</td>
  </tr>
  <tr>
    <td>Domain Registration</td>
    <td style="text-align: center;">1</td>
    <td style="text-align: right;">$200.00</td>
    <td style="text-align: right;">$200.00</td>
  </tr>`,
}

// Function to replace placeholders with agency config data or sample data
function processTemplateForPreview(
  html: string,
  primaryColor: string,
  secondaryColor: string,
  fontFamily: string,
  agencyConfig?: AgencyConfiguration
): string {
  let processed = html

  // Replace styling placeholders
  processed = processed.replace(/\{\{PRIMARY_COLOR\}\}/g, primaryColor || '#000000')
  processed = processed.replace(/\{\{SECONDARY_COLOR\}\}/g, secondaryColor || '#666666')
  processed = processed.replace(/\{\{FONT_FAMILY\}\}/g, fontFamily || 'Arial')

  // Map agency config to placeholder values
  const agencyReplacements: Record<string, string | undefined> = agencyConfig ? {
    // Company info from Agency Configuration
    COMPANY_NAME: agencyConfig.agency_name,
    COMPANY_LOGO: agencyConfig.logo_url,
    COMPANY_ADDRESS_LINE1: agencyConfig.address_line1,
    COMPANY_ADDRESS_LINE2: agencyConfig.address_line2,
    COMPANY_CITY: agencyConfig.city,
    COMPANY_STATE: agencyConfig.state,
    COMPANY_POSTAL_CODE: agencyConfig.postal_code,
    COMPANY_COUNTRY: agencyConfig.country,
    COMPANY_EMAIL: agencyConfig.email,
    COMPANY_PHONE: agencyConfig.phone,
    COMPANY_WEBSITE: agencyConfig.website,
    COMPANY_TAX_ID: agencyConfig.tax_id,
    COMPANY_TAX_REG_NUMBER: agencyConfig.tax_registration_number,
    // Bank details from Agency Configuration Payment Info
    BANK_NAME: agencyConfig.bank_name,
    BANK_ACCOUNT_NAME: agencyConfig.account_name,
    BANK_ACCOUNT_NUMBER: agencyConfig.account_number,
    BANK_ROUTING_NUMBER: agencyConfig.routing_number,
    BANK_SWIFT_CODE: agencyConfig.swift_code,
    BANK_IBAN: agencyConfig.iban,
    PAYMENT_INSTRUCTIONS: agencyConfig.payment_instructions,
  } : {}

  // Replace agency config placeholders
  Object.entries(agencyReplacements).forEach(([key, value]) => {
    const replacement = value || SAMPLE_DATA[key] || ''
    processed = processed.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), replacement)
  })

  // Replace all other data placeholders with sample data
  Object.entries(SAMPLE_DATA).forEach(([key, value]) => {
    if (!agencyReplacements[key]) {
      processed = processed.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value)
    }
  })

  // Helper function to find the matching {{/if}} for a given {{#if FIELD}} tag
  // This properly handles nested conditionals
  const findMatchingEndTag = (str: string, startPos: number): number => {
    let depth = 1
    let pos = startPos
    const ifPattern = /\{\{#if\s+\w+\}\}/g
    const endPattern = /\{\{\/if\}\}/g

    while (depth > 0 && pos < str.length) {
      // Find next {{#if ...}} and {{/if}} from current position
      ifPattern.lastIndex = pos
      endPattern.lastIndex = pos

      const ifMatch = ifPattern.exec(str)
      const endMatch = endPattern.exec(str)

      if (!endMatch) {
        // No more {{/if}} found, return -1
        return -1
      }

      if (ifMatch && ifMatch.index < endMatch.index) {
        // Found a nested {{#if}} before the next {{/if}}
        depth++
        pos = ifMatch.index + ifMatch[0].length
      } else {
        // Found {{/if}}
        depth--
        if (depth === 0) {
          return endMatch.index
        }
        pos = endMatch.index + endMatch[0].length
      }
    }

    return -1
  }

  // Dynamically extract all conditional fields from the template
  // This ensures we process ALL {{#if FIELD}} blocks, not just a hardcoded list
  const conditionalFieldsRegex = /\{\{#if\s+(\w+)\}\}/g
  const conditionalFields = new Set<string>()
  let match
  while ((match = conditionalFieldsRegex.exec(processed)) !== null) {
    conditionalFields.add(match[1])
  }

  // Process each conditional field
  conditionalFields.forEach(field => {
    const startTag = `{{#if ${field}}}`
    const endTag = '{{/if}}'
    // Check if the field has a value from agency config or sample data
    const hasValue = (agencyReplacements[field] || SAMPLE_DATA[field]) &&
                     (agencyReplacements[field] || SAMPLE_DATA[field]) !== ''

    let searchPos = 0
    while (true) {
      const startIdx = processed.indexOf(startTag, searchPos)
      if (startIdx === -1) break

      const contentStart = startIdx + startTag.length
      const endIdx = findMatchingEndTag(processed, contentStart)

      if (endIdx === -1) break

      if (hasValue) {
        // Keep the content, remove the tags
        const content = processed.substring(contentStart, endIdx)
        processed = processed.substring(0, startIdx) + content + processed.substring(endIdx + endTag.length)
        // Continue searching from where we left off (content might have nested conditionals)
        searchPos = startIdx
      } else {
        // Remove the entire block
        processed = processed.substring(0, startIdx) + processed.substring(endIdx + endTag.length)
        searchPos = startIdx
      }
    }
  })

  return processed
}

const DEFAULT_HTML_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: {{FONT_FAMILY}}, sans-serif; margin: 0; padding: 40px; color: #333; }
    .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
    .company-info { text-align: left; }
    .company-name { font-size: 24px; font-weight: bold; color: {{PRIMARY_COLOR}}; }
    .invoice-title { text-align: right; }
    .invoice-title h1 { font-size: 36px; color: {{PRIMARY_COLOR}}; margin: 0; }
    .invoice-number { font-size: 14px; color: {{SECONDARY_COLOR}}; }
    .addresses { display: flex; justify-content: space-between; margin-bottom: 30px; }
    .bill-to, .bill-from { width: 45%; }
    .section-title { font-weight: bold; color: {{PRIMARY_COLOR}}; margin-bottom: 10px; font-size: 12px; text-transform: uppercase; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
    .items-table th { background: {{PRIMARY_COLOR}}; color: white; padding: 12px; text-align: left; }
    .items-table td { padding: 12px; border-bottom: 1px solid #eee; }
    .items-table tr:nth-child(even) { background: #f9f9f9; }
    .totals { text-align: right; margin-top: 20px; }
    .totals-row { display: flex; justify-content: flex-end; margin-bottom: 5px; }
    .totals-label { width: 150px; text-align: right; padding-right: 20px; }
    .totals-value { width: 100px; text-align: right; }
    .total-due { font-size: 18px; font-weight: bold; color: {{PRIMARY_COLOR}}; border-top: 2px solid {{PRIMARY_COLOR}}; padding-top: 10px; }
    .notes { margin-top: 40px; padding: 20px; background: #f5f5f5; border-radius: 5px; }
    .notes-title { font-weight: bold; margin-bottom: 10px; }
    .footer { margin-top: 40px; text-align: center; color: {{SECONDARY_COLOR}}; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="company-info">
      {{#if COMPANY_LOGO}}<img src="{{COMPANY_LOGO}}" alt="Logo" style="max-height: 60px; margin-bottom: 10px;">{{/if}}
      <div class="company-name">{{COMPANY_NAME}}</div>
      <div>{{COMPANY_ADDRESS_LINE1}}</div>
      {{#if COMPANY_ADDRESS_LINE2}}<div>{{COMPANY_ADDRESS_LINE2}}</div>{{/if}}
      <div>{{COMPANY_CITY}}, {{COMPANY_STATE}} {{COMPANY_POSTAL_CODE}}</div>
      <div>{{COMPANY_COUNTRY}}</div>
      <div>{{COMPANY_EMAIL}} | {{COMPANY_PHONE}}</div>
      {{#if COMPANY_TAX_ID}}<div>Tax ID: {{COMPANY_TAX_ID}}</div>{{/if}}
    </div>
    <div class="invoice-title">
      <h1>INVOICE</h1>
      <div class="invoice-number">{{INVOICE_NUMBER}}</div>
      <div style="margin-top: 10px;">
        <div><strong>Date:</strong> {{INVOICE_DATE}}</div>
        <div><strong>Due Date:</strong> {{DUE_DATE}}</div>
      </div>
    </div>
  </div>

  <div class="addresses">
    <div class="bill-to">
      <div class="section-title">Bill To</div>
      <div><strong>{{CLIENT_NAME}}</strong></div>
      <div>{{CLIENT_ADDRESS_LINE1}}</div>
      {{#if CLIENT_ADDRESS_LINE2}}<div>{{CLIENT_ADDRESS_LINE2}}</div>{{/if}}
      <div>{{CLIENT_CITY}}, {{CLIENT_STATE}} {{CLIENT_POSTAL_CODE}}</div>
      <div>{{CLIENT_COUNTRY}}</div>
      <div>{{CLIENT_EMAIL}}</div>
      {{#if CLIENT_PHONE}}<div>{{CLIENT_PHONE}}</div>{{/if}}
    </div>
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th>Description</th>
        <th style="width: 80px; text-align: center;">Qty</th>
        <th style="width: 100px; text-align: right;">Unit Price</th>
        <th style="width: 100px; text-align: right;">Amount</th>
      </tr>
    </thead>
    <tbody>
      {{LINE_ITEMS}}
    </tbody>
  </table>

  <div class="totals">
    <div class="totals-row">
      <div class="totals-label">Subtotal:</div>
      <div class="totals-value">{{CURRENCY_SYMBOL}}{{SUBTOTAL}}</div>
    </div>
    {{#if TAX_AMOUNT}}
    <div class="totals-row">
      <div class="totals-label">Tax ({{TAX_RATE}}%):</div>
      <div class="totals-value">{{CURRENCY_SYMBOL}}{{TAX_AMOUNT}}</div>
    </div>
    {{/if}}
    {{#if DISCOUNT_AMOUNT}}
    <div class="totals-row">
      <div class="totals-label">Discount:</div>
      <div class="totals-value">-{{CURRENCY_SYMBOL}}{{DISCOUNT_AMOUNT}}</div>
    </div>
    {{/if}}
    <div class="totals-row total-due">
      <div class="totals-label">Total Due:</div>
      <div class="totals-value">{{CURRENCY_SYMBOL}}{{TOTAL_AMOUNT}}</div>
    </div>
  </div>

  {{#if NOTES}}
  <div class="notes">
    <div class="notes-title">Notes</div>
    <div>{{NOTES}}</div>
  </div>
  {{/if}}

  {{#if PAYMENT_TERMS}}
  <div class="notes" style="margin-top: 20px;">
    <div class="notes-title">Payment Terms</div>
    <div>{{PAYMENT_TERMS}}</div>
  </div>
  {{/if}}

  <div class="footer">
    Thank you for your business!
  </div>
</body>
</html>`

export default function BillingTemplateFormPage({ mode = 'create' }: BillingTemplateFormPageProps) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const isEditMode = mode === 'edit'
  const isViewMode = mode === 'view'

  // Get template type from URL query param (for create mode)
  const defaultType = (searchParams.get('type') as TemplateType) || 'INVOICE'

  const [showPlaceholderHelp, setShowPlaceholderHelp] = useState(false)
  const [editorMode, setEditorMode] = useState<EditorMode>('preview')
  const [formData, setFormData] = useState<CreateBillingTemplateRequest>({
    template_name: '',
    template_type: defaultType,
    is_default: false,
    primary_color: '#000000',
    secondary_color: '#666666',
    font_family: 'Arial',
    html_template: DEFAULT_HTML_TEMPLATE,
    show_logo: true,
    show_company_details: true,
    show_client_details: true,
    show_invoice_details: true,
    show_line_items: true,
    show_notes: true,
    show_payment_terms: true,
    show_tax_details: true,
    default_notes: '',
    default_payment_terms: 'Net 30',
  })

  // Fetch agency configuration for preview
  const { data: agencyConfig } = useQuery({
    queryKey: ['agencyConfig'],
    queryFn: agencyConfigApi.get,
  })

  // Memoized preview HTML - uses agency config for company/bank placeholders
  const previewHtml = useMemo(() => {
    if (!formData.html_template) return ''
    return processTemplateForPreview(
      formData.html_template,
      formData.primary_color || '#000000',
      formData.secondary_color || '#666666',
      formData.font_family || 'Arial',
      agencyConfig
    )
  }, [formData, agencyConfig])

  // Fetch template for edit/view mode
  const { data: templateData, isLoading } = useQuery({
    queryKey: ['billingTemplate', id],
    queryFn: () => billingTemplatesApi.getById(id!),
    enabled: !!id && (isEditMode || isViewMode),
  })

  // Populate form when template data loads
  useEffect(() => {
    if (templateData && (isEditMode || isViewMode)) {
      setFormData({
        template_name: templateData.template_name,
        template_type: templateData.template_type || 'INVOICE',
        is_default: templateData.is_default,
        primary_color: templateData.primary_color,
        secondary_color: templateData.secondary_color,
        font_family: templateData.font_family,
        html_template: templateData.html_template || DEFAULT_HTML_TEMPLATE,
        show_logo: templateData.show_logo,
        show_company_details: templateData.show_company_details,
        show_client_details: templateData.show_client_details,
        show_invoice_details: templateData.show_invoice_details,
        show_line_items: templateData.show_line_items,
        show_notes: templateData.show_notes,
        show_payment_terms: templateData.show_payment_terms,
        show_tax_details: templateData.show_tax_details,
        default_notes: templateData.default_notes || '',
        default_payment_terms: templateData.default_payment_terms || '',
      })
    }
  }, [templateData, isEditMode, isViewMode])

  const createMutation = useMutation({
    mutationFn: billingTemplatesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingTemplates'] })
      toast.success('Template created successfully')
      navigate('/settings/billing-templates')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error?.details?.error || 'Failed to create template'
      toast.error(message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateBillingTemplateRequest }) =>
      billingTemplatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingTemplates'] })
      queryClient.invalidateQueries({ queryKey: ['billingTemplate', id] })
      toast.success('Template updated successfully')
      navigate('/settings/billing-templates')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.response?.data?.error?.details?.error || 'Failed to update template'
      toast.error(message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Client-side validation
    if (!formData.template_name?.trim()) {
      toast.error('Template name is required')
      return
    }

    if (isEditMode && id) {
      updateMutation.mutate({ id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  if (id && isLoading) {
    return <div className="text-center py-8">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/settings/billing-templates')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {isViewMode ? 'View Template' : isEditMode ? 'Edit Template' : 'Create Template'}
            </h1>
            <p className="text-muted-foreground">
              {isViewMode ? 'View billing template details' : 'Configure billing template with branding and company details'}
            </p>
          </div>
        </div>
        {!isViewMode && (
          <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Template'}
          </Button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="basic">Basic Settings</TabsTrigger>
            <TabsTrigger value="html">
              <Code className="mr-2 h-4 w-4" />
              HTML Template
            </TabsTrigger>
          </TabsList>

          {/* Basic Settings Tab */}
          <TabsContent value="basic" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Template Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="template_name">Template Name *</Label>
                    <Input
                      id="template_name"
                      value={formData.template_name}
                      onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                      placeholder="e.g., Standard Invoice"
                      required
                      disabled={isViewMode}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="template_type">Template Type *</Label>
                    <Select
                      value={formData.template_type}
                      onValueChange={(value: TemplateType) => setFormData({ ...formData, template_type: value })}
                      disabled={isViewMode || isEditMode}
                    >
                      <SelectTrigger id="template_type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INVOICE">Invoice</SelectItem>
                        <SelectItem value="QUOTE">Quote</SelectItem>
                      </SelectContent>
                    </Select>
                    {isEditMode && (
                      <p className="text-xs text-muted-foreground">Template type cannot be changed after creation</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="primary_color">Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="primary_color"
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-12 h-10 p-1"
                        disabled={isViewMode}
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        placeholder="#000000"
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="secondary_color">Secondary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="secondary_color"
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="w-12 h-10 p-1"
                        disabled={isViewMode}
                      />
                      <Input
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        placeholder="#666666"
                        disabled={isViewMode}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="font_family">Font Family</Label>
                  <Input
                    id="font_family"
                    value={formData.font_family}
                    onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}
                    placeholder="Arial"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-3">
                  <Label>Section Visibility</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { key: 'show_logo', label: 'Show Logo' },
                      { key: 'show_company_details', label: 'Company Details' },
                      { key: 'show_client_details', label: 'Client Details' },
                      { key: 'show_invoice_details', label: 'Invoice Details' },
                      { key: 'show_line_items', label: 'Line Items' },
                      { key: 'show_notes', label: 'Notes' },
                      { key: 'show_payment_terms', label: 'Payment Terms' },
                      { key: 'show_tax_details', label: 'Tax Details' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2">
                        <Switch
                          id={key}
                          checked={formData[key as keyof CreateBillingTemplateRequest] as boolean}
                          onCheckedChange={(checked) => setFormData({ ...formData, [key]: checked })}
                          disabled={isViewMode}
                        />
                        <Label htmlFor={key}>{label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="default_payment_terms">Default Payment Terms</Label>
                    <Input
                      id="default_payment_terms"
                      value={formData.default_payment_terms}
                      onChange={(e) => setFormData({ ...formData, default_payment_terms: e.target.value })}
                      placeholder="Net 30"
                      disabled={isViewMode}
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-8">
                    <Switch
                      id="is_default"
                      checked={formData.is_default}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                      disabled={isViewMode}
                    />
                    <Label htmlFor="is_default">Set as Default Template</Label>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="default_notes">Default Notes</Label>
                  <Textarea
                    id="default_notes"
                    value={formData.default_notes}
                    onChange={(e) => setFormData({ ...formData, default_notes: e.target.value })}
                    placeholder="Thank you for your business!"
                    rows={3}
                    disabled={isViewMode}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HTML Template Tab */}
          <TabsContent value="html" className="space-y-4 mt-6">
            <div className="flex gap-2 flex-wrap items-center">
              <div className="flex border rounded-md overflow-hidden">
                <Button
                  type="button"
                  variant={editorMode === 'preview' ? "default" : "ghost"}
                  size="sm"
                  className="rounded-none"
                  onClick={() => setEditorMode('preview')}
                  disabled={isViewMode}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Preview
                </Button>
                <Button
                  type="button"
                  variant={editorMode === 'visual' ? "default" : "ghost"}
                  size="sm"
                  className="rounded-none border-l"
                  onClick={() => setEditorMode('visual')}
                  disabled={isViewMode}
                >
                  <Type className="mr-2 h-4 w-4" />
                  Visual Editor
                </Button>
                <Button
                  type="button"
                  variant={editorMode === 'html' ? "default" : "ghost"}
                  size="sm"
                  className="rounded-none border-l"
                  onClick={() => setEditorMode('html')}
                  disabled={isViewMode}
                >
                  <Code className="mr-2 h-4 w-4" />
                  HTML Source
                </Button>
              </div>
              <Collapsible open={showPlaceholderHelp} onOpenChange={setShowPlaceholderHelp}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Info className="mr-2 h-4 w-4" />
                    {showPlaceholderHelp ? 'Hide' : 'Show'} Placeholders
                  </Button>
                </CollapsibleTrigger>
              </Collapsible>
              {!isViewMode && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({ ...formData, html_template: DEFAULT_HTML_TEMPLATE })}
                >
                  Reset to Default
                </Button>
              )}
            </div>

            <Collapsible open={showPlaceholderHelp} onOpenChange={setShowPlaceholderHelp}>
              <CollapsibleContent>
                <Card className="mb-4">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      Template Placeholders
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate('/settings/agency')}
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        Edit Agency Settings
                      </Button>
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Use these placeholders in your HTML template. Company and bank details come from{' '}
                      <strong>Agency Configuration</strong> settings. Client and Invoice placeholders are replaced with actual data when generating the PDF.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <p className="font-semibold mb-1 text-primary">Company Info</p>
                        <p className="text-muted-foreground text-[10px] mb-1">From Agency Configuration → Branding & Contact</p>
                        <code className="block">{'{{COMPANY_NAME}}'}</code>
                        <code className="block">{'{{COMPANY_LOGO}}'}</code>
                        <code className="block">{'{{COMPANY_EMAIL}}'}</code>
                        <code className="block">{'{{COMPANY_PHONE}}'}</code>
                        <code className="block">{'{{COMPANY_WEBSITE}}'}</code>
                        <p className="text-muted-foreground text-[10px] mt-2 mb-1">From Agency Configuration → Address</p>
                        <code className="block">{'{{COMPANY_ADDRESS_LINE1}}'}</code>
                        <code className="block">{'{{COMPANY_ADDRESS_LINE2}}'}</code>
                        <code className="block">{'{{COMPANY_CITY}}'}</code>
                        <code className="block">{'{{COMPANY_STATE}}'}</code>
                        <code className="block">{'{{COMPANY_POSTAL_CODE}}'}</code>
                        <code className="block">{'{{COMPANY_COUNTRY}}'}</code>
                        <p className="text-muted-foreground text-[10px] mt-2 mb-1">From Agency Configuration → Tax Info</p>
                        <code className="block">{'{{COMPANY_TAX_ID}}'}</code>
                        <code className="block">{'{{COMPANY_TAX_REG_NUMBER}}'}</code>
                      </div>
                      <div>
                        <p className="font-semibold mb-1 text-primary">Bank Details</p>
                        <p className="text-muted-foreground text-[10px] mb-1">From Agency Configuration → Payment Info</p>
                        <code className="block">{'{{BANK_NAME}}'}</code>
                        <code className="block">{'{{BANK_ACCOUNT_NAME}}'}</code>
                        <code className="block">{'{{BANK_ACCOUNT_NUMBER}}'}</code>
                        <code className="block">{'{{BANK_ROUTING_NUMBER}}'}</code>
                        <code className="block">{'{{BANK_SWIFT_CODE}}'}</code>
                        <code className="block">{'{{BANK_IBAN}}'}</code>
                        <code className="block">{'{{PAYMENT_INSTRUCTIONS}}'}</code>
                        <p className="font-semibold mt-3 mb-1">Client Info</p>
                        <p className="text-muted-foreground text-[10px] mb-1">From Invoice Client Data</p>
                        <code className="block">{'{{CLIENT_NAME}}'}</code>
                        <code className="block">{'{{CLIENT_EMAIL}}'}</code>
                        <code className="block">{'{{CLIENT_PHONE}}'}</code>
                        <code className="block">{'{{CLIENT_ADDRESS_LINE1}}'}</code>
                        <code className="block">{'{{CLIENT_ADDRESS_LINE2}}'}</code>
                        <code className="block">{'{{CLIENT_CITY}}'}</code>
                        <code className="block">{'{{CLIENT_STATE}}'}</code>
                        <code className="block">{'{{CLIENT_POSTAL_CODE}}'}</code>
                        <code className="block">{'{{CLIENT_COUNTRY}}'}</code>
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Invoice Details</p>
                        <p className="text-muted-foreground text-[10px] mb-1">From Invoice Data</p>
                        <code className="block">{'{{INVOICE_NUMBER}}'}</code>
                        <code className="block">{'{{INVOICE_DATE}}'}</code>
                        <code className="block">{'{{DUE_DATE}}'}</code>
                        <code className="block">{'{{CURRENCY_SYMBOL}}'}</code>
                        <code className="block">{'{{SUBTOTAL}}'}</code>
                        <code className="block">{'{{TAX_RATE}}'}</code>
                        <code className="block">{'{{TAX_AMOUNT}}'}</code>
                        <code className="block">{'{{DISCOUNT_AMOUNT}}'}</code>
                        <code className="block">{'{{TOTAL_AMOUNT}}'}</code>
                        <code className="block">{'{{BALANCE_DUE}}'}</code>
                        <code className="block">{'{{AMOUNT_PAID}}'}</code>
                        <code className="block">{'{{LINE_ITEMS}}'}</code>
                        <code className="block">{'{{NOTES}}'}</code>
                        <code className="block">{'{{PAYMENT_TERMS}}'}</code>
                      </div>
                      <div>
                        <p className="font-semibold mb-1">Styling</p>
                        <p className="text-muted-foreground text-[10px] mb-1">From Template Settings</p>
                        <code className="block">{'{{PRIMARY_COLOR}}'}</code>
                        <code className="block">{'{{SECONDARY_COLOR}}'}</code>
                        <code className="block">{'{{FONT_FAMILY}}'}</code>
                        <p className="font-semibold mt-3 mb-1">Conditionals</p>
                        <p className="text-muted-foreground text-[10px] mb-1">Show content only if field has value</p>
                        <code className="block">{'{{#if FIELD}}...{{/if}}'}</code>
                        <p className="text-muted-foreground text-[10px] mt-2">Example:</p>
                        <code className="block text-[10px]">{'{{#if BANK_IBAN}}'}</code>
                        <code className="block text-[10px] pl-2">{'IBAN: {{BANK_IBAN}}'}</code>
                        <code className="block text-[10px]">{'{{/if}}'}</code>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>

            {editorMode === 'preview' && (
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label>Preview (with your company data + sample invoice data)</Label>
                  <Badge variant="secondary" className="text-xs">Live Preview</Badge>
                </div>
                <div className="border rounded-lg overflow-hidden bg-white">
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full h-[600px] border-0"
                    title="Invoice Template Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  This preview shows your template with your company details and sample invoice data. The actual invoice will use real client and invoice data.
                </p>
              </div>
            )}

            {editorMode === 'visual' && (
              <div className="grid gap-2">
                <Label>Visual Editor</Label>
                <RichTextEditor
                  content={formData.html_template || ''}
                  onChange={(html) => setFormData({ ...formData, html_template: html })}
                  placeholder="Start designing your invoice template..."
                  className="min-h-[500px]"
                />
                <p className="text-xs text-muted-foreground">
                  Use the toolbar to format your template. For advanced styling, switch to HTML Source mode.
                  Note: Complex layouts may require direct HTML editing.
                </p>
              </div>
            )}

            {editorMode === 'html' && (
              <div className="grid gap-2">
                <Label htmlFor="html_template">HTML Template</Label>
                <Textarea
                  id="html_template"
                  value={formData.html_template}
                  onChange={(e) => setFormData({ ...formData, html_template: e.target.value })}
                  placeholder="Enter your HTML template here..."
                  className="font-mono text-sm min-h-[500px]"
                  rows={25}
                  disabled={isViewMode}
                />
                <p className="text-xs text-muted-foreground">
                  Write your invoice template using HTML and CSS. Use placeholders like {'{{COMPANY_NAME}}'} for dynamic content.
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </form>
    </div>
  )
}

